import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Download,
  CheckCircle,
  AlertTriangle,
  XCircle,
  FileText,
  Clock,
  Mail,
  Star,
} from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Container } from '../components/layout/Container';
import { Button } from '../components/ui/Button';
import { Card, CardContent } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { cn } from '../lib/utils';
import { getChecklistForState } from '../config/checklist';
import {
  getSnapshotResults,
  generateNarratives,
  getPdfDownloadUrl,
  type SnapshotResultsData,
} from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import type { RiskBand } from '../types';

// Build a map of itemId -> text from checklist
function buildChecklistTextMap(stateCode: string): Record<string, string> {
  const categories = getChecklistForState(stateCode);
  const map: Record<string, string> = {};
  for (const cat of categories) {
    for (const item of cat.items) {
      map[item.id] = item.text;
    }
  }
  return map;
}

// Calculate breakdown counts dynamically from checklistResponses (matches PDF generation logic)
function calculateBreakdownFromResponses(
  checklistResponses: Record<string, { status: string; notes?: string }> | undefined
): { compliant: number; atRisk: number; nonCompliant: number; unknown: number } {
  const counts = { compliant: 0, atRisk: 0, nonCompliant: 0, unknown: 0 };

  if (!checklistResponses) return counts;

  for (const key of Object.keys(checklistResponses)) {
    const response = checklistResponses[key];
    if (response?.status) {
      switch (response.status) {
        case 'COMPLIANT':
          counts.compliant++;
          break;
        case 'AT_RISK':
          counts.atRisk++;
          break;
        case 'NON_COMPLIANT':
          counts.nonCompliant++;
          break;
        case 'UNKNOWN':
          counts.unknown++;
          break;
      }
    }
  }

  return counts;
}


export function Results() {
  const [searchParams] = useSearchParams();
  const snapshotId = searchParams.get('snapshotId');
  const { success: showSuccess, error: showError } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState<SnapshotResultsData | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [checklistTextMap, setChecklistTextMap] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const stateCode = results?.agencyProfile?.state || 'VA';

  // Fetch results on mount
  useEffect(() => {
    async function fetchResults() {
      if (!snapshotId) {
        setError('No snapshot ID provided');
        setIsLoading(false);
        return;
      }

      try {
        const data = await getSnapshotResults(snapshotId);
        setResults(data);

        // Build checklist text map for the state
        const stateCode = data.agencyProfile?.state || 'VA';
        const textMap = buildChecklistTextMap(stateCode);
        setChecklistTextMap(textMap);


        // If no narrative exists, try to generate it
        if (!data.narrative) {
          try {
            await generateNarratives(snapshotId);
            // Re-fetch to get updated data with narrative
            const updatedData = await getSnapshotResults(snapshotId);
            setResults(updatedData);
          } catch (narrativeError) {
            console.warn('Narrative generation failed:', narrativeError);
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load results';
        setError(message);
        console.error('Failed to fetch results:', err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchResults();
  }, [snapshotId]);

  // Poll for release status when report is not yet released
  useEffect(() => {
    // Only poll if we have results but PDF not released yet
    if (!results || results.pdf?.releasedAt || isLoading) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const data = await getSnapshotResults(snapshotId!);
        // Check if PDF was released
        if (data.pdf?.releasedAt) {
          setResults(data);
          showSuccess('Your report is ready!');
          clearInterval(pollInterval);
        }
      } catch (err) {
        console.warn('Polling error:', err);
      }
    }, 10000); // Poll every 10 seconds

    return () => clearInterval(pollInterval);
  }, [results, snapshotId, isLoading, showSuccess]);

  const handleDownloadPdf = useCallback(async () => {
    if (!snapshotId) return;

    setIsGeneratingPdf(true);

    try {
      // Get the PDF download URL (only available if released)
      const existing = await getPdfDownloadUrl(snapshotId);
      if (existing.success && existing.downloadUrl) {
        window.open(existing.downloadUrl, '_blank');
        showSuccess('PDF ready for download');
        setIsGeneratingPdf(false);
        return;
      }

      // PDF not available yet
      showError('PDF is still being prepared. Please check back later.');
      setIsGeneratingPdf(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate PDF';
      showError(message);
      setIsGeneratingPdf(false);
    }
  }, [snapshotId, showSuccess, showError]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-sm mx-auto px-4">
            <Spinner size="lg" />
            <p className="mt-4 text-gray-600 text-lg">Loading your results...</p>
            <p className="mt-2 text-gray-500 text-sm">
              This may take a moment.<br />
              Please do not refresh or navigate away.
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Handle pending release (48-hour delay shown to users, 42 hours internal)
  if (results?.status === 'pending_release') {
    // Internal release is at 42 hours, but we show 48 hours to users (6-hour buffer)
    const internalReleaseDate = results.resultsReleaseAt
      ? new Date(results.resultsReleaseAt)
      : null;
    // Add 6 hours for user-facing display (48 - 42 = 6 hour buffer)
    const displayReleaseDate = internalReleaseDate
      ? new Date(internalReleaseDate.getTime() + 6 * 60 * 60 * 1000)
      : null;
    const now = new Date();
    const hoursRemaining = displayReleaseDate
      ? Math.max(0, Math.ceil((displayReleaseDate.getTime() - now.getTime()) / (1000 * 60 * 60)))
      : 48;

    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 flex items-center justify-center py-12">
          <Container>
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-10 h-10 text-teal-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-3">
                  Your Report Is Being Prepared
                </h1>
                <p className="text-gray-600 mb-6">
                  Thank you for completing your compliance assessment! Our team is reviewing your results to ensure accuracy.
                </p>

                <div className="bg-gray-100 rounded-lg p-6 mb-6">
                  <p className="text-sm text-gray-500 mb-2">Estimated availability</p>
                  {displayReleaseDate ? (
                    <div className="text-gray-900">
                      <p className="text-xl font-semibold">
                        {displayReleaseDate.toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                      <p className="text-lg font-medium text-gray-700">
                        {displayReleaseDate.toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xl font-semibold text-gray-900">Within 48 hours</p>
                  )}
                  <p className="text-sm text-gray-500 mt-2">
                    Approximately {hoursRemaining} hour{hoursRemaining !== 1 ? 's' : ''} remaining
                  </p>
                </div>

                <div className="text-sm text-gray-600 mb-2 text-center">
                  <Mail className="w-4 h-4 inline-block mr-2 -mt-0.5" />
                  <span>We'll email you when ready</span>
                </div>
                <p className="text-xs text-gray-400 mb-6 text-center">
                  Didn't receive it? Check your spam folder.
                </p>

                <div className="border-t pt-6">
                  <h3 className="font-medium text-gray-900 mb-3">While you wait</h3>
                  <ul className="text-sm text-gray-600 space-y-2 text-left max-w-md mx-auto">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Gather any compliance documentation you may need</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Review your state's licensing requirements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Prepare your team for potential compliance updates</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </Container>
        </main>
      </div>
    );
  }

  if (error || !results) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {error || 'Results not found'}
            </h2>
            <p className="text-gray-600 mb-4">
              The snapshot may not be completed yet or doesn't exist.
            </p>
            <Link to="/">
              <Button>Go Home</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const { scoring, narrative, agencyProfile, checklistResponses } = results;

  // Instant results (2026-07-24): on-screen score + gaps show immediately.
  // The PDF may still be generating — the download card below handles that
  // state without blocking the results.
  const pdfReady = !!results.pdf?.downloadUrl;

  // Full results (matching PDF structure exactly)

  // Calculate breakdown dynamically from checklistResponses (matches PDF logic - V8 fix)
  const breakdown = calculateBreakdownFromResponses(checklistResponses);

  // V10 FIX: Calculate ALL findings directly from checklistResponses
  // This ensures we show ALL items, not just the limited pre-computed arrays
  type FindingWithStatus = {
    itemId: string;
    text: string;
    category: string;
    riskStatus: 'NON_COMPLIANT' | 'AT_RISK' | 'UNKNOWN';
  };

  const allFindings: FindingWithStatus[] = [];
  for (const [itemId, response] of Object.entries(checklistResponses || {})) {
    if (response.status === 'NON_COMPLIANT' || response.status === 'AT_RISK' || response.status === 'UNKNOWN') {
      allFindings.push({
        itemId,
        text: checklistTextMap[itemId] || itemId,
        category: itemId.split('-')[0], // Extract category prefix (e.g., TX-B1 -> TX-B)
        riskStatus: response.status as 'NON_COMPLIANT' | 'AT_RISK' | 'UNKNOWN',
      });
    }
  }

  // Sort: NON_COMPLIANT first, then UNKNOWN, then AT_RISK
  const priorityOrder: Record<string, number> = { 'NON_COMPLIANT': 0, 'UNKNOWN': 1, 'AT_RISK': 2 };
  allFindings.sort((a, b) => (priorityOrder[a.riskStatus] ?? 2) - (priorityOrder[b.riskStatus] ?? 2));

  const riskBandStyles: Record<RiskBand, { bg: string; text: string; border: string; explanation: string }> = {
    low: {
      bg: 'bg-green-50',
      text: 'text-green-800',
      border: 'border-green-200',
      explanation: 'Your agency shows strong compliance practices. Minor improvements may still enhance your audit readiness.'
    },
    medium: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-800',
      border: 'border-yellow-200',
      explanation: 'Your agency has compliance gaps that could result in citations during an audit. Address immediate items within 30 days.'
    },
    high: {
      bg: 'bg-red-50',
      text: 'text-red-800',
      border: 'border-red-200',
      explanation: 'Your agency has significant compliance gaps. Immediate action is required to avoid potential enforcement actions.'
    },
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-1 py-8 pt-24">
        <Container size="lg">
          {/* PDF Download Card - Top Priority. Non-blocking: results already
              show below; the PDF appears here the moment it finishes. */}
          <Card className={`mb-8 border-2 ${pdfReady ? 'border-green-200 bg-green-50' : 'border-teal-200 bg-teal-50'}`}>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${pdfReady ? 'bg-green-100' : 'bg-teal-100'}`}>
                    {pdfReady
                      ? <Download className="w-6 h-6 text-green-600" />
                      : <Clock className="w-6 h-6 text-teal-600" />}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {pdfReady ? 'Your PDF Report is Ready' : 'Your PDF Report is Generating'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {pdfReady
                        ? `${agencyProfile?.name} • ${stateCode} Compliance Risk Snapshot`
                        : <>We're building your full report — it'll appear here and land in <strong className="break-all">{agencyProfile?.contactEmail}</strong> shortly.</>}
                    </p>
                  </div>
                </div>
                {pdfReady && (
                  <Button
                    onClick={handleDownloadPdf}
                    isLoading={isGeneratingPdf}
                    leftIcon={<Download className="w-4 h-4" />}
                    className="w-full sm:w-auto"
                  >
                    Download PDF Report
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ===== SECTION 1: EXECUTIVE SUMMARY (matches PDF) ===== */}
          <div className="mb-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Executive Summary</h1>

            {/* AI Narrative */}
            {narrative?.executiveSummary && (
              <Card className="mb-6 border-l-4" style={{ borderLeftColor: '#009688' }}>
                <CardContent className="p-6">
                  <p className="text-gray-700 leading-relaxed">{narrative.executiveSummary}</p>
                </CardContent>
              </Card>
            )}

            {/* Key Findings - 4 boxes (matches PDF exactly) */}
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Key Findings</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white border-2 border-green-200 rounded-lg p-4 text-center">
                <div className="text-4xl font-bold text-green-600 mb-1">{breakdown.compliant}</div>
                <div className="text-sm text-gray-600 font-medium">Compliant</div>
              </div>
              <div className="bg-white border-2 border-yellow-200 rounded-lg p-4 text-center">
                <div className="text-4xl font-bold text-yellow-600 mb-1">{breakdown.atRisk}</div>
                <div className="text-sm text-gray-600 font-medium">At Risk</div>
              </div>
              <div className="bg-white border-2 border-red-200 rounded-lg p-4 text-center">
                <div className="text-4xl font-bold text-red-600 mb-1">{breakdown.nonCompliant}</div>
                <div className="text-sm text-gray-600 font-medium">Non-Compliant</div>
              </div>
              <div className="bg-white border-2 border-purple-200 rounded-lg p-4 text-center">
                <div className="text-4xl font-bold text-purple-600 mb-1">{breakdown.unknown}</div>
                <div className="text-sm text-gray-600 font-medium">Unverifiable</div>
              </div>
            </div>

            {/* Unverifiable Warning (matches PDF) */}
            {breakdown.unknown > 0 && (
              <Card className="mb-6 border-2 border-purple-300 bg-purple-50">
                <CardContent className="p-4">
                  <p className="text-purple-800 font-semibold mb-1">
                    ⚠️ {breakdown.unknown} UNVERIFIABLE ITEMS REQUIRE IMMEDIATE ATTENTION
                  </p>
                  <p className="text-purple-700 text-sm">
                    These items cannot be assessed because information was not provided. During a survey, undocumented items are presumed non-compliant.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Risk Interpretation (matches PDF) */}
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Risk Interpretation</h2>
            {(() => {
              // Calculate compliance percentage for Excellent badge
              const totalItems = breakdown.compliant + breakdown.atRisk + breakdown.nonCompliant + breakdown.unknown;
              const compliancePercentage = totalItems > 0 ? Math.round((breakdown.compliant / totalItems) * 100) : 0;
              const isExcellent = compliancePercentage >= 95;

              return (
                <Card className={cn('border-2', riskBandStyles[scoring.riskBand].border, riskBandStyles[scoring.riskBand].bg)}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      {isExcellent && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 font-semibold text-sm">
                          <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                          EXCELLENT
                        </span>
                      )}
                      <span className={cn('text-2xl font-bold', riskBandStyles[scoring.riskBand].text)}>
                        {scoring.riskBand.toUpperCase()} RISK
                      </span>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-700">{compliancePercentage}% Compliant</span>
                    </div>
                    <p className={riskBandStyles[scoring.riskBand].text}>
                      {isExcellent
                        ? 'Outstanding! Your agency demonstrates excellent compliance practices with minimal identified risk areas.'
                        : riskBandStyles[scoring.riskBand].explanation}
                    </p>
                  </CardContent>
                </Card>
              );
            })()}
          </div>

          {/* ===== SECTION 2: REMEDIATION ROADMAP (matches PDF) ===== */}
          {allFindings.length > 0 && (
            <div className="mb-12">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Your Remediation Roadmap</h1>
              <p className="text-gray-600 mb-6">Prioritized action items based on compliance risk and regulatory exposure.</p>

              <div className="space-y-3">
                {allFindings.map((finding, index) => {
                  const isNonCompliant = finding.riskStatus === 'NON_COMPLIANT';
                  const isUnknown = finding.riskStatus === 'UNKNOWN';
                  const bgColor = isNonCompliant ? 'bg-red-50 border-red-200' : isUnknown ? 'bg-purple-50 border-purple-200' : 'bg-yellow-50 border-yellow-200';
                  const iconColor = isNonCompliant ? 'text-red-600' : isUnknown ? 'text-purple-600' : 'text-yellow-600';
                  const badge = isNonCompliant ? '🔴 IMMEDIATE' : isUnknown ? '🟣 VERIFY' : '🟡 SHORT-TERM';

                  return (
                    <div key={finding.itemId} className={cn('p-4 border rounded-lg', bgColor)}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <span className="text-lg font-semibold text-gray-400">{index + 1}</span>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-white border">
                                {badge}
                              </span>
                              <span className="text-xs text-gray-500">{finding.itemId}</span>
                            </div>
                            <p className="font-medium text-gray-900">{finding.text}</p>
                            <p className="text-sm text-gray-500 mt-1">{finding.category}</p>
                          </div>
                        </div>
                        {isNonCompliant ? (
                          <XCircle className={cn('w-5 h-5 flex-shrink-0', iconColor)} />
                        ) : (
                          <AlertTriangle className={cn('w-5 h-5 flex-shrink-0', iconColor)} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ===== SECTION 3: DETAILED FINDINGS HEADER (matches PDF V9) ===== */}
          <div className="my-16 py-12 text-center border-t border-b border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Detailed Findings</h1>
            <div className="w-20 h-1 mx-auto mb-4" style={{ backgroundColor: '#009688' }} />
            <p className="text-gray-600 max-w-md mx-auto">
              Each finding shows your answer, the requirement, the gap, and remediation steps.
            </p>
            <p className="text-sm text-gray-500 mt-4">
              Download the PDF report for complete finding details including regulation citations and remediation guidance.
            </p>
          </div>

          {/* ===== SECTION 4: UPSELL CTA (matches PDF) ===== */}
          <div className="mt-8 p-6 bg-gray-100 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Important Disclaimer
            </h3>
            <p className="text-sm text-gray-600">
              This compliance risk snapshot is for informational purposes only and does
              not constitute legal advice. Compliance requirements vary by provider type
              and jurisdiction. Consult with a qualified compliance professional or
              attorney for specific guidance. Requirements may change; always verify
              current state regulations.
            </p>
          </div>

          {/* Upsell CTA (matches PDF exactly) */}
          {(() => {
            // Get categories for the state
            const categories = getChecklistForState(stateCode);

            // Calculate category groups for the table
            const categoryGroups: Record<string, number> = {};
            allFindings.forEach(f => {
              // First try exact match by item ID
              let category = categories.find(c => c.items.some(item => item.id === f.itemId));

              // If not found, try matching by section letter (e.g., TX-B3 -> B section)
              if (!category) {
                const sectionLetter = f.itemId.replace(/^[A-Z]{2}-/, '').charAt(0); // TX-B3 -> B
                category = categories.find(c => c.id === sectionLetter);
              }

              const categoryName = category?.name || 'Other';
              categoryGroups[categoryName] = (categoryGroups[categoryName] || 0) + 1;
            });

            const allCategoriesWithGaps = Object.entries(categoryGroups)
              .map(([name, count]) => ({ name, count }))
              .sort((a, b) => b.count - a.count);

            // State-specific manual effort mappings based on actual category names
            const getManualEffort = (cat: string): string => {
              const catLower = cat.toLowerCase();
              if (catLower.includes('training') || catLower.includes('competency')) return '10-15 hrs/month tracking';
              if (catLower.includes('personnel') || catLower.includes('file')) return '5-8 hrs/week per hire';
              if (catLower.includes('background') || catLower.includes('screening')) return 'Manual monthly registry checks';
              if (catLower.includes('documentation') || catLower.includes('record')) return 'Spreadsheet tracking chaos';
              if (catLower.includes('licensing') || catLower.includes('operations') || catLower.includes('governance')) return '2-4 hrs/month renewals';
              if (catLower.includes('health') || catLower.includes('safety') || catLower.includes('aide')) return '4-6 hrs/month cert tracking';
              if (catLower.includes('care delivery') || catLower.includes('care') || catLower.includes('service')) return '5-10 hrs/week visit tracking';
              if (catLower.includes('survey') || catLower.includes('compliance')) return '8-12 hrs prep per survey';
              return 'Manual tracking required';
            };

            const getSolution = (cat: string): string => {
              const catLower = cat.toLowerCase();
              if (catLower.includes('training') || catLower.includes('competency')) return 'Auto-tracking + expiry alerts';
              if (catLower.includes('personnel') || catLower.includes('file')) return 'Digital onboarding checklists';
              if (catLower.includes('background') || catLower.includes('screening')) return 'Centralized tracking + alerts';
              if (catLower.includes('documentation') || catLower.includes('record')) return 'Version control + audit trail';
              if (catLower.includes('licensing') || catLower.includes('operations') || catLower.includes('governance')) return 'Renewal reminders + tracking';
              if (catLower.includes('health') || catLower.includes('safety') || catLower.includes('aide')) return 'Cert tracking + auto-alerts';
              if (catLower.includes('care delivery') || catLower.includes('care') || catLower.includes('service')) return 'Visit verification + logging';
              if (catLower.includes('survey') || catLower.includes('compliance')) return 'Always audit-ready files';
              return 'Automated compliance tracking';
            };

            const stateName = stateCode === 'TX' ? 'Texas' : stateCode === 'FL' ? 'Florida' : stateCode;

            return (
              <div className="mt-12 rounded-lg overflow-hidden border border-gray-200">
                {/* Header */}
                <div className="text-center py-6 px-4">
                  <h2 className="text-2xl font-bold mb-2" style={{ color: '#009688' }}>AsiliGuide</h2>
                  <p className="text-xl font-bold text-gray-900 mb-1">You Identified {allFindings.length} Compliance Gaps.</p>
                  <p className="text-xl text-gray-600">Now Close Them—Automatically.</p>
                </div>

                {/* Table: How AsiliGuide Addresses Your Specific Gaps */}
                <div className="px-4 pb-4">
                  <h3 className="font-semibold text-gray-900 mb-3 text-sm">How AsiliGuide Addresses Your Specific Gaps</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="text-left py-2 px-3 border-b border-gray-200 font-medium">Gap Category</th>
                          <th className="text-left py-2 px-3 border-b border-gray-200 font-medium">Manual Effort</th>
                          <th className="text-left py-2 px-3 border-b border-gray-200 font-medium">AsiliGuide Solution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allCategoriesWithGaps.map(({ name, count }) => (
                          <tr key={name}>
                            <td className="py-2 px-3 border-b border-gray-100">
                              <span className="font-medium">{name}</span>{' '}
                              <span className="text-gray-500">({count})</span>
                            </td>
                            <td className="py-2 px-3 border-b border-gray-100 text-red-600">{getManualEffort(name)}</td>
                            <td className="py-2 px-3 border-b border-gray-100 text-green-600">{getSolution(name)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Comparison boxes */}
                <div className="grid grid-cols-2 gap-3 px-4 pb-4">
                  <div className="p-4 bg-red-50 rounded-lg text-center">
                    <p className="font-semibold text-gray-900 mb-2 text-sm">Without AsiliGuide</p>
                    <p className="text-red-600 text-xs mb-1">✗ Manual spreadsheets</p>
                    <p className="text-red-600 text-xs mb-1">✗ Missed deadlines</p>
                    <p className="text-red-600 text-xs">✗ Hours of admin work</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center border-2" style={{ borderColor: '#009688' }}>
                    <p className="font-semibold text-sm mb-2" style={{ color: '#009688' }}>With AsiliGuide</p>
                    <p className="text-green-600 text-xs mb-1">✓ Automated alerts</p>
                    <p className="text-green-600 text-xs mb-1">✓ {stateName}-specific checklists</p>
                    <p className="text-green-600 text-xs">✓ Minutes per week</p>
                  </div>
                </div>

                {/* Pricing */}
                <div className="text-center py-4 px-4 bg-gray-50">
                  <p className="text-2xl font-bold" style={{ color: '#009688' }}>$199/month</p>
                  <p className="text-gray-500 text-sm">Cancel anytime • No setup fees • Start at: <strong>asiliguide.com</strong></p>
                </div>

                {/* Get Started */}
                <div className="mx-4 mb-4 p-4 bg-green-50 rounded-lg border-l-4" style={{ borderLeftColor: '#009688' }}>
                  <p className="font-semibold text-gray-900 mb-1">Ready to fix these gaps?</p>
                  <p className="text-sm text-gray-700">AsiliGuide automates compliance tracking so you never fall behind. <strong>Start at asiliguide.com</strong></p>
                </div>
              </div>
            );
          })()}
        </Container>
      </main>

    </div>
  );
}
